import { useEffect, useState } from "react";
import { crmApi } from "../../lib/crm";
import { Headphones, TrendingUp, Award, AlertTriangle, RefreshCw } from "lucide-react";

const RESULT_COLORS = {
  booked: "text-emerald-300 bg-emerald-500/15 border-emerald-500/30",
  callback: "text-amber-300 bg-amber-500/15 border-amber-500/30",
  disqualified: "text-rose-300 bg-rose-500/15 border-rose-500/30",
  voicemail: "text-sky-300 bg-sky-500/15 border-sky-500/30",
  no_answer: "text-white/50 bg-white/5 border-white/15",
  gatekeeper: "text-violet-300 bg-violet-500/15 border-violet-500/30",
  no_fit: "text-rose-300 bg-rose-500/15 border-rose-500/30",
  other: "text-white/60 bg-white/5 border-white/15",
};
const RESULT_LABEL = {
  booked: "Booked",
  callback: "Callback",
  disqualified: "Disqualified",
  voicemail: "Voicemail",
  no_answer: "No answer",
  gatekeeper: "Gatekeeper",
  no_fit: "No-fit",
  other: "Other",
};

export default function CRMCallStrategies() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, r] = await Promise.all([
        crmApi.get(`/call-strategies/analytics?days=${days}`),
        crmApi.get(`/call-strategies/recent?limit=80`),
      ]);
      setData(a.data);
      setRecent(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const totals = data?.totals || { calls: 0, booked: 0, disqualified: 0, win_rate: 0, avg_score: 0 };
  const variants = data?.variants || [];
  const agents = data?.agents || [];
  const topVariant = variants[0];

  return (
    <div className="space-y-6 p-6" data-testid="call-strategies-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Headphones className="w-7 h-7 text-fuchsia-400" />
            Call strategies
          </h1>
          <p className="text-sm text-white/50 mt-1">
            Which opening variant works best — broken down by variant, agent and result class.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="strategies-days-select"
            className="bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-sm text-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </select>
          <button
            type="button"
            onClick={load}
            data-testid="strategies-refresh"
            className="px-3 py-1.5 rounded-md bg-lime-400/15 text-lime-300 border border-lime-400/30 hover:bg-lime-400/25 text-sm font-semibold flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          label="Calls logged"
          value={totals.calls}
          icon={<Headphones className="w-4 h-4" />}
          tone="indigo"
          testid="kpi-calls"
        />
        <KpiCard
          label="Win rate"
          value={`${totals.win_rate}%`}
          sub={`${totals.booked} booked`}
          icon={<TrendingUp className="w-4 h-4" />}
          tone="emerald"
          testid="kpi-winrate"
        />
        <KpiCard
          label="Avg qualification"
          value={`${totals.avg_score}/5`}
          icon={<Award className="w-4 h-4" />}
          tone="amber"
          testid="kpi-avgscore"
        />
        <KpiCard
          label="Disqualified"
          value={totals.disqualified}
          icon={<AlertTriangle className="w-4 h-4" />}
          tone="rose"
          testid="kpi-disqualified"
        />
      </div>

      {/* Top variant highlight */}
      {topVariant && (
        <div
          className="rounded-xl border border-fuchsia-500/30 bg-gradient-to-r from-fuchsia-500/10 to-violet-500/10 p-5 flex items-center justify-between flex-wrap gap-3"
          data-testid="top-variant-card"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-fuchsia-300 font-semibold">
              Most-used variant
            </div>
            <div className="text-xl font-bold mt-1">
              {topVariant.variant_n ? `#${topVariant.variant_n} · ` : ""}
              {topVariant.variant_name}
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Stat label="Calls" value={topVariant.total} />
            <Stat label="Win rate" value={`${topVariant.win_rate}%`} highlight />
            <Stat label="Avg score" value={`${topVariant.avg_score}/5`} />
          </div>
        </div>
      )}

      {/* Per-variant table */}
      <section className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-white/70">
            Performance by variant
          </h2>
          <span className="text-xs text-white/40">Sorted by usage</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-white/40 border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3">Variant</th>
                <th className="text-right px-3 py-3">Total</th>
                <th className="text-right px-3 py-3 text-emerald-300">Booked</th>
                <th className="text-right px-3 py-3 text-amber-300">Callback</th>
                <th className="text-right px-3 py-3 text-rose-300">Disqualified</th>
                <th className="text-right px-3 py-3 text-sky-300">Voicemail</th>
                <th className="text-right px-3 py-3 text-white/50">No answer</th>
                <th className="text-right px-3 py-3">Win rate</th>
                <th className="text-right px-3 py-3">Avg score</th>
              </tr>
            </thead>
            <tbody data-testid="variants-table-body">
              {variants.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-white/40 px-4 py-8">
                    No calls logged yet in the selected period.
                  </td>
                </tr>
              )}
              {variants.map((v) => (
                <tr
                  key={`${v.variant_n}-${v.variant_name}`}
                  className="border-b border-white/5 hover:bg-white/[0.02]"
                  data-testid={`variant-row-${v.variant_n}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold">
                      {v.variant_n ? <span className="text-fuchsia-300 mr-2">#{v.variant_n}</span> : null}
                      {v.variant_name}
                    </div>
                  </td>
                  <td className="text-right px-3 py-3 mono">{v.total}</td>
                  <td className="text-right px-3 py-3 text-emerald-300 mono">{v.booked}</td>
                  <td className="text-right px-3 py-3 text-amber-300 mono">{v.callback}</td>
                  <td className="text-right px-3 py-3 text-rose-300 mono">{v.disqualified}</td>
                  <td className="text-right px-3 py-3 text-sky-300 mono">{v.voicemail}</td>
                  <td className="text-right px-3 py-3 text-white/50 mono">{v.no_answer}</td>
                  <td className="text-right px-3 py-3">
                    <WinRateBar pct={v.win_rate} />
                  </td>
                  <td className="text-right px-3 py-3 mono">{v.avg_score}/5</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Per-agent table */}
      <section className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-white/70">
            Performance by agent
          </h2>
          <span className="text-xs text-white/40">Sorted by win rate</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-white/40 border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3">Agent</th>
                <th className="text-right px-3 py-3">Calls</th>
                <th className="text-right px-3 py-3 text-emerald-300">Booked</th>
                <th className="text-right px-3 py-3 text-rose-300">Disqualified</th>
                <th className="text-right px-3 py-3">Win rate</th>
                <th className="text-right px-3 py-3">Avg score</th>
              </tr>
            </thead>
            <tbody data-testid="agents-table-body">
              {agents.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-white/40 px-4 py-8">
                    No agent activity yet.
                  </td>
                </tr>
              )}
              {agents.map((a) => (
                <tr key={a.agent_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 font-semibold">{a.agent_name}</td>
                  <td className="text-right px-3 py-3 mono">{a.total}</td>
                  <td className="text-right px-3 py-3 text-emerald-300 mono">{a.booked}</td>
                  <td className="text-right px-3 py-3 text-rose-300 mono">{a.disqualified}</td>
                  <td className="text-right px-3 py-3">
                    <WinRateBar pct={a.win_rate} />
                  </td>
                  <td className="text-right px-3 py-3 mono">{a.avg_score}/5</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Recent activity */}
      <section className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10">
          <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-white/70">
            Recent post-call cards
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-white/40 border-b border-white/10">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-3 py-3">Agent</th>
                <th className="text-left px-3 py-3">Lead</th>
                <th className="text-left px-3 py-3">Variant</th>
                <th className="text-left px-3 py-3">Result</th>
                <th className="text-right px-3 py-3">Score</th>
                <th className="text-left px-3 py-3">Notes</th>
              </tr>
            </thead>
            <tbody data-testid="recent-outcomes-body">
              {recent.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-white/40 px-4 py-8">
                    No post-call cards saved yet — they'll appear here once agents click "Save to CRM" inside the assistant.
                  </td>
                </tr>
              )}
              {recent.map((o) => (
                <tr key={o.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">
                    {new Date(o.created_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-3">{o.agent_name || "—"}</td>
                  <td className="px-3 py-3 font-semibold">{o.lead_label || "—"}</td>
                  <td className="px-3 py-3 text-white/70">
                    {o.variant_n ? <span className="text-fuchsia-300 mr-1.5">#{o.variant_n}</span> : null}
                    {o.variant_name || "—"}
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${
                        RESULT_COLORS[o.result_class] || RESULT_COLORS.other
                      }`}
                    >
                      {RESULT_LABEL[o.result_class] || o.result_class}
                    </span>
                  </td>
                  <td className="text-right px-3 py-3 mono">{o.score}/5</td>
                  <td className="px-3 py-3 text-white/50 text-xs max-w-[400px] truncate">
                    {o.notes || o.result_detail || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function KpiCard({ label, value, sub, icon, tone = "indigo", testid }) {
  const tones = {
    indigo: "from-indigo-500/10 to-blue-500/10 border-indigo-500/20 text-indigo-300",
    emerald: "from-emerald-500/10 to-green-500/10 border-emerald-500/20 text-emerald-300",
    amber: "from-amber-500/10 to-yellow-500/10 border-amber-500/20 text-amber-300",
    rose: "from-rose-500/10 to-red-500/10 border-rose-500/20 text-rose-300",
  };
  return (
    <div
      className={`rounded-xl border bg-gradient-to-br p-4 ${tones[tone]}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-semibold opacity-80">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold mt-2 text-white">{value}</div>
      {sub && <div className="text-xs opacity-60 mt-1">{sub}</div>}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className={`text-lg font-bold mt-0.5 ${highlight ? "text-fuchsia-300" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function WinRateBar({ pct }) {
  const w = Math.max(0, Math.min(100, pct));
  const tone =
    w >= 30 ? "from-emerald-400 to-emerald-500" :
    w >= 15 ? "from-amber-400 to-amber-500" :
    "from-rose-400 to-rose-500";
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="w-24 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${tone}`} style={{ width: `${w}%` }} />
      </div>
      <span className="mono text-xs w-10 text-right">{w}%</span>
    </div>
  );
}
